// Decodifica un "encoded polyline" de Google (algoritmo publico) a una lista
// de [lat, lng]. Implementacion en JS puro para no depender de la libreria
// `geometry` de Google Maps (que no se carga en este proyecto).
export const decodePolyline = (encoded) => {
    if (!encoded || typeof encoded !== 'string') return [];
    const puntos = [];
    let indice = 0;
    let lat = 0;
    let lng = 0;
    while (indice < encoded.length) {
        let resultado = 0;
        let desplazamiento = 0;
        let b;
        do {
            b = encoded.charCodeAt(indice++) - 63;
            resultado |= (b & 0x1f) << desplazamiento;
            desplazamiento += 5;
        } while (b >= 0x20);
        const deltaLat = (resultado & 1) ? ~(resultado >> 1) : (resultado >> 1);
        lat += deltaLat;

        resultado = 0;
        desplazamiento = 0;
        do {
            b = encoded.charCodeAt(indice++) - 63;
            resultado |= (b & 0x1f) << desplazamiento;
            desplazamiento += 5;
        } while (b >= 0x20);
        const deltaLng = (resultado & 1) ? ~(resultado >> 1) : (resultado >> 1);
        lng += deltaLng;

        puntos.push([lat / 1e5, lng / 1e5]);
    }
    return puntos;
};

// Codifica una lista de [lat, lng] al formato encoded polyline de Google.
// Implementacion en JS puro (sin depender de la libreria `geometry`).
const _encodeSigned = (n) => {
    let signed = n < 0 ? ~(n << 1) : (n << 1);
    let encoded = '';
    while (signed >= 0x20) {
        encoded += String.fromCharCode((0x20 | (signed & 0x1f)) + 63);
        signed >>= 5;
    }
    encoded += String.fromCharCode(signed + 63);
    return encoded;
};

export const encodePolyline = (puntos) => {
    if (!Array.isArray(puntos) || puntos.length === 0) return '';
    let lastLat = 0;
    let lastLng = 0;
    let result = '';
    for (const par of puntos) {
        const lat = Array.isArray(par) ? par[0] : par.lat;
        const lng = Array.isArray(par) ? par[1] : par.lng;
        const latE5 = Math.round(lat * 1e5);
        const lngE5 = Math.round(lng * 1e5);
        result += _encodeSigned(latE5 - lastLat);
        result += _encodeSigned(lngE5 - lastLng);
        lastLat = latE5;
        lastLng = lngE5;
    }
    return result;
};

const RADIO_TIERRA_KM = 6371;

const toRad = (deg) => (deg * Math.PI) / 180;

// Distancia en km entre dos puntos lat/lng (haversine).
export const haversineKm = (lat1, lng1, lat2, lng2) => {
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return RADIO_TIERRA_KM * c;
};

// Aproximacion plana valida para distancias cortas: proyecta lat/lng a un
// plano equirectangular escalado y devuelve la distancia minima del punto
// al segmento (en km).
const distanciaPuntoSegmentoKm = (lat, lng, latA, lngA, latB, lngB) => {
    const latRefRad = toRad((latA + latB) / 2);
    const kmPorGradoLat = 111.32;
    const kmPorGradoLng = 111.32 * Math.cos(latRefRad);

    const px = (lng - lngA) * kmPorGradoLng;
    const py = (lat - latA) * kmPorGradoLat;
    const sx = (lngB - lngA) * kmPorGradoLng;
    const sy = (latB - latA) * kmPorGradoLat;

    const longitudCuadrada = sx * sx + sy * sy;
    if (longitudCuadrada === 0) {
        return Math.sqrt(px * px + py * py);
    }
    let t = (px * sx + py * sy) / longitudCuadrada;
    t = Math.max(0, Math.min(1, t));
    const dx = px - t * sx;
    const dy = py - t * sy;
    return Math.sqrt(dx * dx + dy * dy);
};

// Distancia minima en km de un punto a una polilinea (lista de [lat, lng]).
// Si la polilinea es vacia o tiene un solo punto, devuelve Infinity.
export const distanciaKmPuntoAPolilinea = (lat, lng, polilinea) => {
    if (!Array.isArray(polilinea) || polilinea.length < 2) return Infinity;
    let min = Infinity;
    for (let i = 0; i < polilinea.length - 1; i++) {
        const [latA, lngA] = polilinea[i];
        const [latB, lngB] = polilinea[i + 1];
        const d = distanciaPuntoSegmentoKm(lat, lng, latA, lngA, latB, lngB);
        if (d < min) min = d;
    }
    return min;
};

// Devuelve true si el punto esta a <= radioKm de la ruta. Si la ruta tiene
// polilinea (encoded), usa esa; si no, hace fallback a un segmento recto
// origen-destino. Si tampoco hay origen/destino, devuelve false.
export const puntoCercaDeRuta = (lat, lng, ruta) => {
    if (!ruta) return false;
    const radio = typeof ruta.radioKm === 'number' && ruta.radioKm > 0 ? ruta.radioKm : 1;
    const polilinea = decodePolyline(ruta.polilineaEncoded);
    if (polilinea.length >= 2) {
        return distanciaKmPuntoAPolilinea(lat, lng, polilinea) <= radio;
    }
    if (
        typeof ruta.origenLat === 'number' && typeof ruta.origenLng === 'number' &&
        typeof ruta.destinoLat === 'number' && typeof ruta.destinoLng === 'number'
    ) {
        const d = distanciaPuntoSegmentoKm(
            lat, lng,
            ruta.origenLat, ruta.origenLng,
            ruta.destinoLat, ruta.destinoLng
        );
        return d <= radio;
    }
    return false;
};
